import React, { useEffect, useState } from "react";
import { kp } from "../karkhana/karkhanaPaths";
import { homeAfterLogin, isKarkhanaSupervisor, supervisorMayOpen } from "../../helpers/karkhanaEnabled";
import { Link, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import jwt from "jwt-decode";
import notifications from "../../common/Components/Notifications/Notification";
import { checkLoginUser } from "../../Store/auth/login/actions";
import { clearStudioWorkspaceCaches } from "../../helpers/studioSignupSession";
import { dropRememberedPhone } from "../karkhana/supervisor/supervisorSession";
import { isCollectorEnabled } from "../../helpers/collectorSession";
import { takeReturnTo } from "../sales/internalQr";
import SiteLayout from "../website/site/SiteLayout";
import loomImg from "../website/assets/images/site/front-page-1-3.jpg";

/** The backend's login messages, worded for people. */
const friendlyError = (message?: string) => {
  if (!message) return "Could not log in. Please try again.";
  if (/does not\s+exist/i.test(message)) return "No Studio account uses this email or phone number.";
  if (/authentication failed/i.test(message)) return "Incorrect password.";
  return message;
};

const readSavedLogin = (): string => {
  try {
    return JSON.parse(localStorage.getItem("UsersLoginDeatils") || "null")?.email || "";
  } catch {
    return "";
  }
};

const Login = (props: any) => {
  const history = useHistory();
  const [email, setEmail] = useState(readSavedLogin);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const savedRemember = localStorage.getItem("rememberMe");
  const [remember, setRemember] = useState(savedRemember === "true");

  // Remember me keeps the email or phone only; older builds also stored the password, so drop it.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("UsersLoginDeatils") || "null");
      if (saved?.password !== undefined) {
        localStorage.setItem("UsersLoginDeatils", JSON.stringify({ email: saved?.email || "" }));
      }
    } catch {
      localStorage.removeItem("UsersLoginDeatils");
    }
  }, []);

  const onRemember = (checked: boolean) => {
    setRemember(checked);
    localStorage.setItem("rememberMe", String(checked));
    if (checked) localStorage.setItem("UsersLoginDeatils", JSON.stringify({ email }));
    else localStorage.removeItem("UsersLoginDeatils");
  };

  const validate = () => {
    const next: { email?: string; password?: string } = {};
    if (!email.trim()) next.email = "Email or phone is required.";
    if (!password) next.password = "Password is required.";
    setErrors(next);
    const firstId = next.email ? "login-id" : next.password ? "login-password" : "";
    if (firstId) window.setTimeout(() => document.getElementById(firstId)?.focus(), 0);
    return !next.email && !next.password;
  };

  const handleSubmit = () => {
    if (loading) return;
    localStorage.removeItem("response");
    setResponse("");
    if (!validate()) return;
    setLoading(true);
    props.checkLoginUser({ emailOrPhone: email.toLowerCase().trim(), password }, (res: any) => {
      setLoading(false);
      if (!res?.success) {
        setResponse(friendlyError(res?.message));
        return;
      }
      const authUser: any = jwt(res?.data?.token);
      localStorage.setItem("authUser", JSON.stringify(authUser));
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      // A supervisor's remembered phone key must never renew this person's session.
      dropRememberedPhone();
      clearStudioWorkspaceCaches();
      localStorage.setItem("rememberMe", String(remember));
      if (remember) localStorage.setItem("UsersLoginDeatils", JSON.stringify({ email }));
      notifications({ title: "Success!", message: res?.message, type: "success" });
      const returnTo = takeReturnTo();
      if (isKarkhanaSupervisor(authUser?.users?.role)) {
        // Supervisors only have the daily rounds.
        history.push(supervisorMayOpen(returnTo || "") ? (returnTo as string) : kp.rounds());
        return;
      }
      history.push(returnTo && returnTo.startsWith("/") ? returnTo : homeAfterLogin(authUser?.users?.role));
    });
  };

  return (
    <SiteLayout title="Studio log in — HastKala Pramanak" minimal minimalLink={{ to: "/studio/signup", label: "Start free studio" }}>
      <section className="su">
        <div className="s-wrap su__grid">
          <div className="su__card">
            <span className="s-eyebrow">Studio</span>
            <h1>Welcome back</h1>
            <p className="su__lead">Log in with the email or phone number you used for your studio.</p>
            <p className="su__required-note"><span className="su-req" aria-hidden="true">*</span> Required</p>

            {response && <p className="su-banner" role="alert">{response}</p>}

            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
            >
              <div className={`su-field${errors.email ? " has-error" : ""}`}>
                <label htmlFor="login-id">
                  Email or phone<span className="su-req" aria-hidden="true">*</span>
                </label>
                <input
                  id="login-id"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prev) => ({ ...prev, email: "" }));
                    setResponse("");
                  }}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "login-id-error" : undefined}
                />
                {errors.email && <p className="su-error" id="login-id-error" role="alert">{errors.email}</p>}
              </div>

              <div className={`su-field${errors.password ? " has-error" : ""}`}>
                <div className="su-label-row">
                  <label htmlFor="login-password">
                    Password<span className="su-req" aria-hidden="true">*</span>
                  </label>
                  <Link to="/forget-password">Forgot password?</Link>
                </div>
                <div className="su-password">
                  <input
                    id="login-password"
                    name="password"
                    type={show ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors((prev) => ({ ...prev, password: "" }));
                      setResponse("");
                    }}
                    required
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? "login-password-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    aria-label={show ? "Hide password" : "Show password"}
                    aria-pressed={show}
                  >
                    {show ? "Hide" : "Show"}
                  </button>
                </div>
                {errors.password && (
                  <p className="su-error" id="login-password-error" role="alert">{errors.password}</p>
                )}
              </div>

              <div className="su-terms">
                <label>
                  <input type="checkbox" checked={remember} onChange={(e) => onRemember(e.target.checked)} />
                  <span>Remember my email or phone on this device</span>
                </label>
              </div>

              <button type="submit" className="s-btn s-btn--primary su__submit" disabled={loading} aria-busy={loading}>
                {loading ? "Logging in…" : "Log in"}
              </button>
            </form>

            <p className="su__alt">
              New studio? <Link to="/studio/signup">Start a free 7-day trial</Link>
            </p>
            {isCollectorEnabled() && (
              <p className="su__alt su__alt--tight">
                Looking for My Collection? <Link to="/my-collection/login">Log in with your phone</Link>
              </p>
            )}
          </div>

          <aside className="su__side" aria-label="About Studio">
            <img src={loomImg} alt="A weaver's hands working at a handloom" />
            <div className="su__benefits">
              <div><b>Studio</b><span>Product records, certificates, NFC and QR tags</span></div>
              <div><b>Karkhana</b><span>Looms, daily work and wages for the floor</span></div>
              <div><b>Catalogue</b><span>Share collections you control</span></div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
};

const mapStateToProps = (state: any) => {
  return {
    ...state,
  };
};
export default connect(mapStateToProps, {
  checkLoginUser,
})(Login);
